/**
 * CMS uploads live on the API origin (/uploads/...). The storefront must not use
 * relative /uploads paths as Next/Image src (that hits the Next dev server, not the API).
 * Full absolute URLs from the API must use unoptimized Image or be in next.config remotePatterns.
 */

const API_BASE = (process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000").replace(/\/$/, "");

export function resolveCmsImageSrc(url: string | null | undefined, fallback: string): string {
  const u = url?.trim();
  if (!u) return fallback;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("data:")) return u;
  // Public files in the Next app (not the API)
  if (u.startsWith("/media/")) return u;
  return u.startsWith("/") ? `${API_BASE}${u}` : `${API_BASE}/${u}`;
}

/** Local static assets under /media are served by Next; everything else is API/external. */
export function cmsImageUnoptimized(resolvedSrc: string): boolean {
  return !resolvedSrc.startsWith("/media/");
}

/**
 * Rewrite relative `/uploads/...` image sources inside a block of CMS HTML to
 * absolute API-origin URLs. Body content authored in the admin TinyMCE editor
 * (e.g. images embedded from an imported .docx) is stored with relative
 * `/uploads/blog/...` paths so the DB stays hostname-agnostic. When that HTML is
 * dropped into the storefront via dangerouslySetInnerHTML, a relative src would
 * resolve against the Next server (which doesn't serve /uploads) and 404 — so we
 * point it at the API origin here. Absolute/external srcs are left untouched.
 */
export function resolveCmsHtmlImages(html: string | null | undefined): string {
  if (!html) return "";
  return html.replace(
    /(<img\b[^>]*?\bsrc=["'])(\/uploads\/[^"']*)(["'])/gi,
    (_m, pre, path, quote) => `${pre}${API_BASE}${path}${quote}`,
  );
}
